import { Directive, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { Router } from '@angular/router';
import { PassportDetailsStore } from './passport-details.store';
import { UiStateService } from './ui-state.service';
import { CommentsPanelService } from './comments-panel.service';
import { HistoryPanelService } from './history-panel.service';
import { ScrollPositionService } from './scroll-position.service';

/**
 * Base class for passport cell components (Scheduler, Transloading, Gatehouse, Shunting)
 * Contains all shared logic to eliminate code duplication
 * 
 * All child components use the shared template: '../shared/passport-cell.template.html'
 * This ensures 100% consistency across all persona passport cells while maintaining
 * the ability to customize behavior through TypeScript getters and overrides.
 */
@Directive()
export abstract class BasePassportCellComponent implements OnInit {
  @Input() recordId = '';
  @Input() loadNumber = '';
  @Input() scheduledText = '';
  @Input() vendorName = '';
  @Input() variety = '';
  @Input() bulkerNumber = '';
  @Input() haulier = '';
  @Input() origin = '';
  @Input() destination = '';
  @Input() apn = '';
  @Input() apnName = '';
  @Input() vendorNumber = '';
  @Input() status: 'pending' | 'outgoing' | 'completed' = 'pending';
  @Input() styleState: 'default' | 'pcr-change' | 'cancelled' | 'state-2' = 'default';

  @Output() expand = new EventEmitter<void>();

  isExpanded = false;

  /**
   * Abstract property that child classes must implement
   * Returns the persona name for navigation (e.g., 'scheduler', 'transloading')
   */
  protected abstract get personaName(): string;

  /**
   * Meta items for template iteration
   */
  get metaItems() {
    return [
      { icon: '/assets/images/Grower_Icon.png', alt: 'Grower', cssClass: 'grower', value: this.vendorName },
      { icon: '/assets/images/Plant_Variety.png', alt: 'Variety', cssClass: 'variety', value: this.variety },
      { icon: '/assets/images/Bulker_Icon.png', alt: 'Bulker', cssClass: 'bulker', value: this.bulkerNumber }
    ];
  }

  /**
   * Primary label for the details button
   * Override in child classes if needed
   */
  get detailsPrimaryLabel(): 'Add' | 'View' {
    return 'View';
  }

  /**
   * Whether to hide the "Add" button in passport details
   * Override in child classes if needed
   */
  get detailsHideAdd(): boolean {
    return false;
  }

  /**
   * Whether to hide the "Passport Complete" button in passport details
   * Override in child classes if needed
   */
  get detailsHidePassportComplete(): boolean {
    return false;
  }

  constructor(
    protected readonly router: Router,
    protected readonly details: PassportDetailsStore,
    protected readonly ui: UiStateService
  ) {}

  protected readonly commentsPanelService = inject(CommentsPanelService);
  protected readonly historyPanelService = inject(HistoryPanelService);
  protected readonly scrollPositionService = inject(ScrollPositionService);

  /**
   * Helper to get record ID with fallback
   */
  protected getRecordId(fallback: string = ''): string {
    return this.recordId || this.loadNumber || fallback;
  }

  /**
   * Helper to create passport header for navigation
   */
  protected createPassportHeader() {
    return {
      loadNumber: this.loadNumber,
      variety: this.variety,
      haulier: this.haulier,
      bulkerNumber: this.bulkerNumber,
      scheduledText: this.scheduledText,
      origin: this.origin,
      destination: this.destination,
      apnName: this.apnName,
      vendorName: this.vendorName,
      vendorNumber: this.vendorNumber
    };
  }

  ngOnInit(): void {
    const id = this.getRecordId();
    if (this.ui.expandedRecordId() === id) {
      this.isExpanded = true;
    }
  }

  onToggleExpand(): void {
    const id = this.getRecordId();
    const shouldExpand = !(this.ui.expandedRecordId() === id && this.isExpanded);
    this.isExpanded = shouldExpand;
    this.expand.emit();
    
    if (this.isExpanded) {
      this.ui.setExpandedRecord(id);
    } else if (this.ui.expandedRecordId() === id) {
      this.ui.setExpandedRecord('');
    }
  }

  /**
   * Returns the data to upsert into the details store
   * Can be overridden by child classes if needed
   */
  protected getDetailsData(id: string): any {
    return {
      id,
      loadNumber: this.loadNumber,
      origin: this.origin,
      destination: this.destination,
      vendorName: this.vendorName,
      vendorNumber: this.vendorNumber,
      apnName: this.apnName,
      bulkerNumber: this.bulkerNumber,
      haulier: this.haulier
    };
  }

  openDetails(): void {
    const id = this.getRecordId('unknown');
    const existing = this.details.get(id);
    if (!existing) {
      this.details.upsert(this.getDetailsData(id));
    }
    this.ui.setExpandedRecord(id);
    // Save scroll position and set returning flag before navigating (mobile only)
    this.scrollPositionService.saveScrollPosition(`/${this.personaName}`);
    this.scrollPositionService.setReturningFromDetail(true);
    void this.router.navigate(['/passport', id], { state: { from: this.personaName } });
  }

  openComments(): void {
    const id = this.getRecordId();
    const header = this.createPassportHeader();
    
    // Check if desktop view (window width >= 768px)
    const isDesktop = globalThis.window !== undefined && globalThis.window.innerWidth >= 768;
    
    if (isDesktop) {
      // Use service to open comments panel for desktop
      this.commentsPanelService.openCommentsPanel(id, header);
    } else {
      // Save scroll position and set returning flag before navigating (mobile only)
      this.scrollPositionService.saveScrollPosition(`/${this.personaName}`);
      this.scrollPositionService.setReturningFromDetail(true);
      // Navigate to comments page for mobile
      void this.router.navigate(['/comments'], { state: { passportId: id, header } });
    }
  }

  openHistory(): void {
    const id = this.getRecordId();
    const header = this.createPassportHeader();
    
    // Check if desktop view (window width >= 768px)
    const isDesktop = globalThis.window !== undefined && globalThis.window.innerWidth >= 768;
    
    if (isDesktop) {
      // Use service to open history panel for desktop
      this.historyPanelService.openHistoryPanel(id, header);
    } else {
      // Save scroll position and set returning flag before navigating (mobile only)
      this.scrollPositionService.saveScrollPosition(`/${this.personaName}`);
      this.scrollPositionService.setReturningFromDetail(true);
      // Navigate to history page for mobile
      void this.router.navigate(['/history'], { state: { passportId: id, header } });
    }
  }
}

